import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { auth } from "config";
import { API_BASE_URL } from "config";
import { useAuth } from "contexts/AuthContext";

// Query keys
export const QUERY_KEYS = {
  BILLING_PACKAGES: "billing-packages",
  USER_PROFILE: "user-profile",
  USER_GENERATIONS: "user-generations",
};

// API functions
async function fetchWithAuth(url, options = {}) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("Must be signed in to make this request");
  }

  const idToken = await user.getIdToken();

  const response = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || `Request failed: ${response.status}`);
  }

  return response.json();
}

export async function fetchBillingPackages() {
  const response = await fetch(`${API_BASE_URL}/billing`);

  if (!response.ok) {
    throw new Error(`Failed to fetch packages: ${response.status}`);
  }

  const data = await response.json();

  const transform = (plan) => ({
    id: plan.price_id,
    amount: plan.credits,
    productName: plan.product_name,
    productType: plan.product_type,
    mostPopular: plan.most_popular,
    pricing: plan.pricing,
    formattedPrice: `$${(plan.pricing / 100).toFixed(0)}`,
  });

  return {
    subscriptions: (data.subscriptions || []).map(transform),
    topups: (data.topups || []).map(transform),
  };
}

export async function fetchUserProfile() {
  return await fetchWithAuth(`${API_BASE_URL}/user`);
}

export async function createOrGetUser() {
  return await fetchWithAuth(`${API_BASE_URL}/user/me`, {
    method: "POST",
  });
}

export async function createCheckoutSession(priceId) {
  const data = await fetchWithAuth(
    `${API_BASE_URL}/billing/create-checkout-session`,
    {
      method: "POST",
      body: JSON.stringify({ price_id: priceId }),
    }
  );
  return data.url;
}

export async function createBillingPortalSession() {
  const data = await fetchWithAuth(`${API_BASE_URL}/billing/billing-portal`, {
    method: "POST",
    body: JSON.stringify({}),
  });
  return data.url;
}

export async function fetchUserGenerations(cursor = "0", limit = 12) {
  const url = new URL(`${API_BASE_URL}/assets`);
  url.searchParams.set("cursor", String(cursor ?? "0"));
  url.searchParams.set("limit", String(limit ?? 12));
  return await fetchWithAuth(url.toString());
}

// Custom hooks
export function useBillingPackages() {
  return useQuery({
    queryKey: [QUERY_KEYS.BILLING_PACKAGES],
    queryFn: fetchBillingPackages,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useUserProfile() {
  const { user } = useAuth();

  return useQuery({
    queryKey: [QUERY_KEYS.USER_PROFILE, user?.uid],
    queryFn: fetchUserProfile,
    enabled: !!user, // Use stable user from AuthContext instead of auth.currentUser
    staleTime: 2 * 60 * 1000, // 2 minutes (shorter than default)
    refetchOnWindowFocus: true, // Enable refetch when user returns to tab
  });
}

export function useCreateOrGetUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createOrGetUser,
    onSuccess: (data) => {
      // Update the user profile cache
      queryClient.setQueryData([QUERY_KEYS.USER_PROFILE], data);
    },
  });
}

export function useCreateCheckoutSession() {
  return useMutation({
    mutationFn: createCheckoutSession,
    onSuccess: (url) => {
      // Open Stripe checkout in a new tab to preserve drawing state
      window.open(url, "_blank", "noopener,noreferrer");
    },
  });
}

export function useCreateBillingPortalSession() {
  return useMutation({
    mutationFn: createBillingPortalSession,
    onSuccess: (url) => {
      // Open Stripe billing portal in a new tab to preserve drawing state
      window.open(url, "_blank", "noopener,noreferrer");
    },
  });
}

export function useUserGenerations(cursor, limit = 12) {
  const { user } = useAuth();

  return useQuery({
    queryKey: [QUERY_KEYS.USER_GENERATIONS, user?.uid, cursor, limit],
    queryFn: () => fetchUserGenerations(cursor, limit),
    enabled: !!user,
    staleTime: 60 * 1000, // 1 minute
    refetchOnWindowFocus: true,
    keepPreviousData: true,
  });
}
